import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import locale from 'antd/locale/en_US';
import { Capacitor } from '@capacitor/core';
import './App.css';
import './mobile-responsive.css';
import './app-theme.css';
import Login from './components/Login';
import ErrorBoundary from './components/ErrorBoundary';
import ConnectionStatus from './components/ConnectionStatus';
import { ToastProvider } from './components/Toast';
import InstallPrompt from './components/InstallPrompt';
import LoadingSkeleton from './components/LoadingSkeleton';
import OperatorWatermark from './components/OperatorWatermark';
import { AuthProvider, useAuth } from './context/AuthContext';
import api from './services/api';
import RoleRedirect from './components/RoleRedirect';
import RoleSelectionMenu from './components/RoleSelectionMenu';
import { useRolePermissions } from './hooks/useRolePermissions';
import { ROUTES, isRouteAccessible } from './constants/routes';
import GeofenceStatusBanner from './components/GeofenceStatusBanner';
import GeofenceLockScreen from './components/GeofenceLockScreen';

// Lazy load heavy components for better performance
const Wizard = lazy(() => import('./components/Wizard'));
const RawMaterialScreen = lazy(() => import('./components/RawMaterialScreen'));
const RawMaterialFinalScreen = lazy(() => import('./components/RawMaterialFinalScreen'));
const ReturnMaterialScreen = lazy(() => import('./components/ReturnMaterialScreen'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const ExecutiveDashboard = lazy(() => import('./components/ExecutiveDashboard'));
const AnalysisDashboard = lazy(() => import('./components/AnalysisDashboard'));
const ConsumptionLogScreen = lazy(() => import('./components/ConsumptionLogScreen'));
const ProcurementScreen = lazy(() => import('./components/ProcurementScreen'));
const QualityScreen = lazy(() => import('./components/quality/QualityScreenRedesigned'));
const ExtrusionScreen = lazy(() => import('./components/ExtrusionScreen'));
const FurnaceScreen = lazy(() => import('./components/FurnaceScreen'));
const FurnaceStockScreen = lazy(() => import('./components/FurnaceStockScreen'));
const RollingScreen = lazy(() => import('./components/RollingScreen'));
const DrawScreen = lazy(() => import('./components/DrawScreen'));
const DispatchScreen = lazy(() => import('./components/DispatchScreen'));
const DispatchNewScreen = lazy(() => import('./components/DispatchNewScreen'));
const DispatchProductionLogsScreen = lazy(() => import('./components/DispatchProductionLogsScreen'));
const ReturnDispatchScreen = lazy(() => import('./components/ReturnDispatchScreen'));
const DayWorkingReportScreen = lazy(() => import('./components/DayWorkingReportScreen'));
const VMCScreen = lazy(() => import('./components/VMCScreen'));
const PackingScreen = lazy(() => import('./components/PackingScreen'));
const PackingNewScreen = lazy(() => import('./components/PackingNewScreen'));
const ElectricityConsumptionScreen = lazy(() => import('./components/ElectricityConsumptionScreen'));
const OrderScreen = lazy(() => import('./components/OrderScreen'));
const ClientManagement = lazy(() => import('./components/ClientManagement'));
const PlanningDashboard = lazy(() => import('./components/PlanningDashboard'));
const ProductionDashboardScreen = lazy(() => import('./components/ProductionDashboardScreen'));
const ProductionExecutionScreen = lazy(() => import('./components/ProductionExecutionScreen'));
const ScrapScreen = lazy(() => import('./components/ScrapScreen'));
const MeltingScrapScreen = lazy(() => import('./components/meltingScrap/MeltingScrapScreen'));
const InternalRejectionScreen = lazy(() => import('./components/InternalRejectionScreen'));
const DiePlannerScreen = lazy(() => import('./components/DiePlannerScreen'));
const DieStoreScreen = lazy(() => import('./components/dieManagement/DieStoreScreen'));
const BoxPlannerScreen = lazy(() => import('./components/BoxPlannerScreen'));
const WorkOrderPlanner = lazy(() => import('./components/WorkOrderPlanner'));
const PurchaseOrderScreen = lazy(() => import('./components/PurchaseOrderScreen'));
const StockManagementScreen = lazy(() => import('./components/StockManagementScreen'));
const MaintenancePlannerScreen = lazy(() => import('./components/MaintenancePlannerScreen'));
const ToolHealthCheckScreen = lazy(() => import('./components/ToolHealthCheckScreen'));
const MaintenancePlanningScreen = lazy(() => import('./components/MaintenancePlanningScreen'));
const MaintenanceDashboard = lazy(() => import('./components/maintenance/dashboard/MaintenanceDashboard'));
const MaintenanceAnalytics = lazy(() => import('./components/maintenance/analytics/MaintenanceAnalytics'));
const DepartmentManagement = lazy(() => import('./components/DepartmentManagement'));
const DepartmentManagementNew = lazy(() => import('./components/maintenance/departments/DepartmentManagement'));
const MachineManagement = lazy(() => import('./components/MachineManagement'));
const MachineManagementNew = lazy(() => import('./components/maintenance/machines/MachineManagement'));
const ToolManagement = lazy(() => import('./components/ToolManagement'));
const ToolManagementNew = lazy(() => import('./components/maintenance/tools/ToolManagement'));
const MaintenanceTaskManagement = lazy(() => import('./components/MaintenanceTaskManagement'));
const MaintenanceTaskManagementNew = lazy(() => import('./components/maintenance/tasks/TaskManagement'));
const MaintenancePlanManagement = lazy(() => import('./components/MaintenancePlanManagement'));
const MaintenancePlanManagementNew = lazy(() => import('./components/maintenance/plans/PlanManagement'));
const PhotosManagementScreen = lazy(() => import('./components/PhotosManagementScreen'));
const OfflinePhotoGallery = lazy(() => import('./components/OfflinePhotoGallery'));
const BreakdownRequestScreen = lazy(() => import('./components/BreakdownRequestScreen'));
const BreakdownRequestManagement = lazy(() => import('./components/BreakdownRequestManagement'));
const TaxonomyManagement = lazy(() => import('./components/maintenance/breakdown/TaxonomyManagement'));
const ItemTrackingScreen = lazy(() => import('./components/maintenance/breakdown/ItemTrackingScreen'));
const TaxonomyPMScreen = lazy(() => import('./components/maintenance/breakdown/TaxonomyPMScreen'));
const MaintenanceBrdDashboard = lazy(() => import('./components/maintenance/breakdown/MaintenanceBrdDashboard'));
const EquipmentHistoryScreen = lazy(() => import('./components/maintenance/breakdown/EquipmentHistoryScreen'));
const GeofenceManagement = lazy(() => import('./components/admin/GeofenceManagement'));
const PackingDashboard = lazy(() => import('./components/PackingDashboard'));
const AiAssistantScreen = lazy(() => import('./components/AiAssistantScreen'));
const PartyComparisonDashboard = lazy(() => import('./components/PartyComparisonDashboard'));
const ProductionTracker = lazy(() => import('./components/ProductionTracker'));
const ProductionReports = lazy(() => import('./components/ProductionReports'));
const CasterDashboard = lazy(() => import('./components/caster/CasterDashboard'));
const FurnaceMaterialScreen = lazy(() => import('./components/FurnaceMaterialScreen'));
const FurnaceDashboardScreen = lazy(() => import('./components/FurnaceDashboardScreen'));
const DailyFurnaceUsageScreen = lazy(() => import('./components/DailyFurnaceUsageScreen'));
const JobworkPortal = lazy(() => import('./components/jobwork/JobworkPortal'));
const JobworkWizard = lazy(() => import('./components/jobwork/JobworkWizard'));
const JobworkDispatchScreen = lazy(() => import('./components/jobwork/JobworkDispatchScreen'));
const DeletionApprovalsScreen = lazy(() => import('./components/DeletionApprovalsScreen'));
const SmartWeightPilotScreen = lazy(() => import('./components/SmartWeightPilotScreen'));
const DebitNoteScreen = lazy(() => import('./components/DebitNoteScreen'));
const AppGallery = lazy(() => import('./components/AppGallery'));
const ProcessScenariosScreen = lazy(() => import('./components/breakdown/ProcessScenariosScreen'));
const QuickBreakdownTicketPage = lazy(() => import('./components/breakdown/QuickBreakdownTicketPage'));
const BreakdownHub = lazy(() => import('./components/breakdown/BreakdownHub'));
const BrdApplicationPage = lazy(() => import('./components/breakdown/BrdApplicationPage'));
const BrdRepairablePage = lazy(() => import('./components/breakdown/BrdRepairablePage'));
const BrdCostingPage = lazy(() => import('./components/breakdown/BrdCostingPage'));
const BrdVendorPage = lazy(() => import('./components/breakdown/BrdVendorPage'));
const BrdStorePage = lazy(() => import('./components/breakdown/BrdStorePage'));
const CustomerFeedbackForm = lazy(() => import('./components/CustomerFeedbackForm'));
const FeedbackDashboard = lazy(() => import('./components/FeedbackDashboard'));

function AppContent() {
  const { operator, auth, handleLogin, geofenceStatus, geofenceLoading, geofenceError } = useAuth();
  const permissions = useRolePermissions(operator);
  const location = useLocation();
  // Force remount on navigation. This makes routing robust in cases where
  // the URL changes but the visible screen doesn't update (seen on some webviews/APKs).
  const routesKey = location.key || location.pathname;

  // Strict geofence enforcement:
  // - Native app only: when authenticated, the app is blocked unless inside authorized geofence.
  // - Exception: Offline Photo Gallery is always accessible.
  const isOfflinePhotosPath = location.pathname === ROUTES.OFFLINE_PHOTOS;
  const isSelectRolePath = location.pathname === ROUTES.SELECT_ROLE;
  const isAuthenticated = Boolean(auth && operator);
  const isNativeApp = Capacitor.isNativePlatform();
  const isInsideGeofence = geofenceStatus?.inside === true;
  // Only lock after we have a geofence result (or an error). This prevents accidental blocking
  // while the first location check is still in-flight.
  const hasGeofenceDecision = Boolean(geofenceError || geofenceStatus);
  const shouldGeofenceLock =
    isAuthenticated &&
    isNativeApp &&
    !permissions.isAdmin &&
    hasGeofenceDecision &&
    !isOfflinePhotosPath &&
    !isSelectRolePath &&
    !isInsideGeofence;

  if (shouldGeofenceLock) {
    return (
      <>
        <Routes key={routesKey}>
          <Route
            path={ROUTES.OFFLINE_PHOTOS}
            element={
              <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                <OfflinePhotoGallery operator={operator} auth={auth} />
              </Suspense>
            }
          />
          <Route path="/*" element={<GeofenceLockScreen />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      <Routes key={routesKey}>
        <Route path="/customer-feedback" element={<CustomerFeedbackForm />} />
        {/* Quick Breakdown Ticket — standalone public page, no operator login required */}
        <Route
          path={ROUTES.QUICK_TICKET}
          element={
            <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
              <QuickBreakdownTicketPage operator={operator} />
            </Suspense>
          }
        />
        {!auth && <Route path="/*" element={<Login onLogin={handleLogin} />} />}
        {auth && (
          <>
          {/* Default index route: redirect authenticated operators to select-role */}
          <Route path="/" element={<Navigate to={ROUTES.SELECT_ROLE} replace />} />

          {/* Role Selection / Menu - available to all authenticated operators */}
          {operator && (
            <Route
              path={ROUTES.SELECT_ROLE}
              element={
                <RoleSelectionMenu
                  operator={operator}
                  onRoleSelected={(roleSlug) => {
                    // Role selection handled inside component
                  }}
                />
              }
            />
          )}
          {permissions.isAdmin && operator && (
            <Route 
              path={ROUTES.ADMIN} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <AdminDashboard operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {permissions.isAdmin && operator && (
            <Route 
              path="/feedback-dashboard" 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <FeedbackDashboard />
                </Suspense>
              } 
            />
          )}
          {permissions.isAdmin && operator && (
            <Route
              path={ROUTES.GEOFENCES}
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <GeofenceManagement operator={operator} auth={auth} onBack={() => window.history.back()} />
                </Suspense>
              }
            />
          )}
          {permissions.isAdmin && operator && (
            <Route
              path={ROUTES.DELETION_APPROVALS}
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <DeletionApprovalsScreen operator={operator} auth={auth} onBack={() => window.history.back()} />
                </Suspense>
              }
            />
          )}
          {permissions.isAdmin && operator && (
            <Route
              path={ROUTES.SMART_WEIGHT_PILOT}
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <SmartWeightPilotScreen operator={operator} />
                </Suspense>
              }
            />
          )}
          {permissions.isAdmin && operator && (
            <Route 
              path={ROUTES.EXECUTIVE} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <ExecutiveDashboard operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {permissions.isAdmin && operator && (
            <Route
              path={ROUTES.ANALYSIS}
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <AnalysisDashboard operator={operator} auth={auth} />
                </Suspense>
              }
            />
          )}
          {(permissions.hasConsumptionRole || permissions.hasConsumptionCostRole) && operator && (
            <Route 
              path={ROUTES.CONSUMPTION} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <ConsumptionLogScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {permissions.hasProcurementRole && operator && (
            <Route 
              path={ROUTES.PROCUREMENT} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <ProcurementScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {permissions.hasQualityRole && operator && (
            <Route 
              path={ROUTES.QUALITY} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <QualityScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {permissions.hasExtrusionRole && operator && (
            <Route 
              path={ROUTES.EXTRUSION} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <ExtrusionScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {permissions.hasFurnaceRole && operator && (
            <Route 
              path={ROUTES.FURNACE} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <FurnaceScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {permissions.hasFurnaceRole && operator && (
            <Route 
              path={ROUTES.FURNACE_STOCK} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <FurnaceStockScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {permissions.hasFurnaceRole && operator && (
            <Route 
              path={ROUTES.CASTER} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <CasterDashboard operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {permissions.hasFurnaceRole && operator && (
            <Route 
              path={ROUTES.FURNACE_MATERIAL} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <FurnaceMaterialScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {(permissions.hasScrapRole || permissions.hasInternalRejectionRole || permissions.isAdmin) && operator && (
            <>
            <Route 
              path={ROUTES.DAILY_FURNACE_USAGE} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <DailyFurnaceUsageScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
            <Route 
              path={ROUTES.FURNACE_DASHBOARD} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <FurnaceDashboardScreen />
                </Suspense>
              } 
            />
            </>
          )}
          {(permissions.hasFurnaceRole || permissions.isAdmin) && operator && (
            <Route
              path={ROUTES.DEBIT_NOTE}
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <DebitNoteScreen operator={operator} auth={auth} />
                </Suspense>
              }
            />
          )}
          {permissions.hasRollingRole && operator && (
            <Route 
              path={ROUTES.ROLLING} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <RollingScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {permissions.hasDrawRole && operator && (
            <Route 
              path={ROUTES.DRAW} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <DrawScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {(permissions.hasProductionDashboardRole || permissions.isAdmin) && operator && (
            <Route 
              path={ROUTES.PRODUCTION_DASHBOARD} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <ProductionDashboardScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {permissions.hasDispatchRole && operator && (
            <>
              <Route 
                path={ROUTES.DISPATCH_RETURN} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <ReturnDispatchScreen operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
              <Route 
                path={ROUTES.DISPATCH_NEW} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <DispatchNewScreen operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
              <Route 
                path={ROUTES.DISPATCH_PRODUCTION_LOGS} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <DispatchProductionLogsScreen operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
              <Route
                path={ROUTES.PACKING_DASHBOARD}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <PackingDashboard operator={operator} auth={auth} />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.STOCK_MANAGEMENT}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <StockManagementScreen operator={operator} auth={auth} />
                  </Suspense>
                }
              />
              <Route 
                path={ROUTES.DISPATCH} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <DispatchScreen operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
            </>
          )}
          {permissions.hasDayWorkingRole && operator && (
            <Route 
              path={ROUTES.DAY_WORKING} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <DayWorkingReportScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {permissions.hasVMCRole && operator && (
            <Route 
              path={ROUTES.VMC} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <VMCScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {(permissions.isAdmin || permissions.hasExtrusionRole || permissions.hasRollingRole || permissions.hasDrawRole || permissions.hasVMCRole || permissions.hasPackingRole) && operator && (
            <Route
              path={ROUTES.PRODUCTION_REPORTS}
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <ProductionReports operator={operator} auth={auth} />
                </Suspense>
              }
            />
          )}
          {permissions.hasPackingRole && operator && (
            <>
              <Route 
                path={ROUTES.PACKING_NEW} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <PackingNewScreen operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
              <Route 
                path={ROUTES.PACKING} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <PackingScreen operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
            </>
          )}
          {permissions.hasElectricityConsumptionRole && operator && (
            <Route 
              path={ROUTES.ELECTRICITY_CONSUMPTION} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <ElectricityConsumptionScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {(permissions.hasOrderRole || permissions.hasPurchaseOrderRole) && operator && (
            <>
              <Route 
                path={ROUTES.ORDERS} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <OrderScreen operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
              <Route 
                path={ROUTES.CLIENTS} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <ClientManagement operator={operator} auth={auth} onBack={() => window.history.back()} />
                  </Suspense>
                } 
              />
              <Route 
                path={ROUTES.PLANNING} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <PlanningDashboard operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
              <Route
                path={ROUTES.PRODUCTION_TRACKER}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <ProductionTracker operator={operator} auth={auth} />
                  </Suspense>
                }
              />
              <Route 
                path={ROUTES.BOX_PLANNER} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <BoxPlannerScreen operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
              <Route 
                path={ROUTES.PARTY_COMPARISON} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <PartyComparisonDashboard operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
              <Route 
                path={ROUTES.WORK_ORDER_PLANNER} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <WorkOrderPlanner operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
              <Route 
                path={ROUTES.PRODUCTION} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <ProductionExecutionScreen operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
              <Route 
                path={ROUTES.PURCHASE_ORDERS} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <PurchaseOrderScreen operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
            </>
          )}
          {permissions.hasScrapRole && operator && (
            <Route 
              path={ROUTES.SCRAP} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <ScrapScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {(permissions.hasScrapRole || permissions.hasFurnaceRole || permissions.hasRawMaterialRole) && operator && (
            <Route 
              path={ROUTES.MELTING_SCRAP} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <MeltingScrapScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {permissions.hasInternalRejectionRole && operator && (
            <Route 
              path={ROUTES.INTERNAL_REJECTION} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <InternalRejectionScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {permissions.hasDiePlannerRole && operator && (
            <Route 
              path={ROUTES.DIE_PLANNER} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <DiePlannerScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {isRouteAccessible(ROUTES.DIE_STORE, operator) && (
            <Route 
              path={ROUTES.DIE_STORE} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <DieStoreScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
          )}
          {/* Maintenance Module Routes */}
          {(permissions.hasMaintenanceRole || permissions.hasMaintenanceMechanicalRole || permissions.hasMaintenanceElectricalRole) && operator && (
            <>
              <Route 
                path={ROUTES.MAINTENANCE_PLANNER} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <MaintenancePlannerScreen operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
              <Route
                path={ROUTES.MAINTENANCE_ITEM_TRACKING}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <ItemTrackingScreen operator={operator} onBack={() => window.history.back()} />
                  </Suspense>
                }
              />
            </>
          )}
          {operator && (
            <>
              <Route 
                path={ROUTES.OFFLINE_PHOTOS} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <OfflinePhotoGallery operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
              <Route 
                path={ROUTES.BREAKDOWN_REQUEST} 
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <BreakdownRequestScreen operator={operator} auth={auth} />
                </Suspense>
              } 
            />
              <Route
                path={ROUTES.BREAKDOWN_PROCESS}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <BreakdownHub operator={operator} />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.BRD_FORM1}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <BrdApplicationPage operator={operator} />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.BRD_FORM2}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <BrdRepairablePage operator={operator} />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.BRD_FORM3}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <BrdCostingPage operator={operator} />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.BRD_FORM4}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <BrdVendorPage operator={operator} />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.BRD_STORE}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <BrdStorePage operator={operator} />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.BRD_SCENARIOS}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <ProcessScenariosScreen operator={operator} />
                  </Suspense>
                }
              />
            </>
          )}
          {permissions.hasMaintenanceRole && operator && (
            <>
              <Route 
                path={ROUTES.MAINTENANCE} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <MaintenanceDashboard operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
              <Route 
                path={ROUTES.MAINTENANCE_DEPARTMENTS} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <DepartmentManagementNew operator={operator} auth={auth} onBack={() => window.history.back()} />
                  </Suspense>
                } 
              />
              <Route 
                path={ROUTES.MAINTENANCE_MACHINES} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <MachineManagementNew operator={operator} auth={auth} onBack={() => window.history.back()} />
                  </Suspense>
                } 
              />
              <Route 
                path={ROUTES.MAINTENANCE_TOOLS} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <ToolManagementNew operator={operator} auth={auth} onBack={() => window.history.back()} />
                  </Suspense>
                } 
              />
              <Route 
                path={ROUTES.MAINTENANCE_TASKS} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <MaintenanceTaskManagementNew operator={operator} auth={auth} onBack={() => window.history.back()} />
                  </Suspense>
                } 
              />
              <Route 
                path={ROUTES.MAINTENANCE_PLANS} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <MaintenancePlanManagementNew operator={operator} auth={auth} onBack={() => window.history.back()} />
                  </Suspense>
                } 
              />
              <Route 
                path={ROUTES.MAINTENANCE_BREAKDOWN_REQUESTS} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <BreakdownRequestManagement operator={operator} auth={auth} onBack={() => window.history.back()} />
                  </Suspense>
                } 
              />
              <Route
                path={ROUTES.MAINTENANCE_TAXONOMY}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <TaxonomyManagement operator={operator} onBack={() => window.history.back()} />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.MAINTENANCE_ITEM_TRACKING}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <ItemTrackingScreen operator={operator} onBack={() => window.history.back()} />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.MAINTENANCE_TAXONOMY_PM}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <TaxonomyPMScreen operator={operator} onBack={() => window.history.back()} />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.MAINTENANCE_BRD_DASHBOARD}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <MaintenanceBrdDashboard operator={operator} onBack={() => window.history.back()} />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.MAINTENANCE_EQUIPMENT_HISTORY}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <EquipmentHistoryScreen operator={operator} onBack={() => window.history.back()} />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.MAINTENANCE_ANALYTICS}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <MaintenanceAnalytics operator={operator} auth={auth} onBack={() => window.history.back()} />
                  </Suspense>
                }
              />
            </>
          )}
          {permissions.hasAiAssistantRole && operator && (
            <Route
              path={ROUTES.AI_ASSISTANT}
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <AiAssistantScreen operator={operator} />
                </Suspense>
              }
            />
          )}
          {operator && (
            <Route
              path={ROUTES.APP_GALLERY}
              element={
                <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                  <AppGallery operator={operator} />
                </Suspense>
              }
            />
          )}
          {permissions.hasJobworkRole && operator && (
            <>
              <Route 
                path={ROUTES.JOBWORK} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <JobworkPortal operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
              <Route 
                path={ROUTES.JOBWORK_WIZARD} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <JobworkWizard operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
              <Route 
                path={ROUTES.JOBWORK_DISPATCH} 
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <JobworkDispatchScreen operator={operator} auth={auth} />
                  </Suspense>
                } 
              />
            </>
          )}
          {(permissions.hasRawMaterialRole || permissions.isAdmin || operator) && (
            <>
              <Route
                path={ROUTES.PHOTOS_MANAGEMENT}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <PhotosManagementScreen operator={operator} auth={auth} />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.RAW_MATERIAL_WIZARD}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <Wizard operator={operator} auth={auth} />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.RAW_MATERIAL_FINAL}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <RawMaterialFinalScreen operator={operator} auth={auth} />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.RAW_MATERIAL_RETURN}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <ReturnMaterialScreen operator={operator} auth={auth} />
                  </Suspense>
                }
              />
              <Route
                path={ROUTES.RAW_MATERIAL}
                element={
                  <Suspense fallback={<div className="container"><LoadingSkeleton type="card" /></div>}>
                    <RawMaterialScreen operator={operator} auth={auth} />
                  </Suspense>
                }
              />
            </>
          )}
          {operator && (
            <Route 
              path="/*" 
              element={<Navigate to={ROUTES.SELECT_ROLE} replace />} 
            />
          )}
        </>
      )}
      {auth && !operator && (
        <Route 
          path="/*" 
          element={
            <div className="container">
              <div className="card">
                <LoadingSkeleton type="card" />
              </div>
            </div>
          } 
        />
      )}
    </Routes>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ConfigProvider
        locale={locale}
        theme={{
          token: {
            colorPrimary: '#0ea5e9',
            colorSuccess: '#10b981',
            colorWarning: '#f59e0b',
            colorError: '#ef4444',
            borderRadius: 8,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Inter", sans-serif',
          },
        }}
      >
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ToastProvider>
            <AuthProvider>
              <ConnectionStatus />
              <OperatorWatermark />
              <AppContent />
              <InstallPrompt />
            </AuthProvider>
          </ToastProvider>
        </Router>
      </ConfigProvider>
    </ErrorBoundary>
  );
}

export default App;
